import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button } from 'components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/Card'
import { Badge } from 'components/ui/Badge'
import { Input } from 'components/ui/Input'
import { Dropdown } from 'components/ui/Dropdown'
import { PageHeader } from 'components/layout/PageHeader'
import { UserForm } from 'features/users/components/UserForm'
import { RoleAssignmentModal } from 'features/users/components/RoleAssignmentModal'
import { DeleteUserModal } from 'features/users/components/DeleteUserModal'
import { UserProfileModal } from 'features/users/components/UserProfileModal'
import { 
  userManagementApi, 
  rolesApi,
  type UserWithRoles, 
  type Role
} from 'lib/api'
import { useToast } from 'contexts/ToastContext'
import { useAuth } from 'contexts/AuthContext'
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search,
  User,
  Mail,
  UserCheck,
  Filter,
  CheckCircle2,
  XCircle,
  Loader2,
  UserCircle,
  Settings
} from 'lucide-react'


export function UserManagement() {
  const { t } = useTranslation()
  const { addToast } = useToast()
  const { user: currentUser } = useAuth()
  const navigate = useNavigate()
  
  const [users, setUsers] = useState<UserWithRoles[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterRole, setFilterRole] = useState<string>('')
  const [filterStatus, setFilterStatus] = useState<string>('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showRoleModal, setShowRoleModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showProfileModal, setShowProfileModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<UserWithRoles | null>(null)
  const [deletingUser, setDeletingUser] = useState(false)
  const [updatingStatusUserId, setUpdatingStatusUserId] = useState<number | null>(null)

  // Table pagination
  const [page, setPage] = useState(1)
  const pageSize = 10

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      const [usersRes, rolesRes] = await Promise.all([
        userManagementApi.getUsers(),
        rolesApi.getRoles()
      ])
      
      const usersData = Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data as any)?.results || []
      const rolesData = Array.isArray(rolesRes.data) ? rolesRes.data : (rolesRes.data as any)?.results || []
      
      // Backend already filters users by organization, so no need to filter here
      setUsers(usersData)
      setRoles(rolesData)
    } catch (error) {
      console.error('Error fetching data:', error)
      setUsers([])
      setRoles([])
      addToast({
        type: 'error',
        title: t('users.loadErrorTitle'),
        message: t('users.loadErrorMessage'),
        duration: 5000
      })
    } finally {
      setLoading(false)
    }
  }

  const handleFormSuccess = () => {
    fetchData()
  }

  const handleDeleteUser = async () => {
    if (!selectedUser) return

    try {
      setDeletingUser(true)
      await userManagementApi.deleteUser(selectedUser.id)
      setUsers(users.filter(u => u.id !== selectedUser.id))
      setShowDeleteModal(false)
      setSelectedUser(null)
      addToast({
        type: 'success',
        title: t('users.successTitle'),
        message: t('users.messages.userDeleted'),
        duration: 3000
      })
    } catch (error: any) {
      console.error('Error deleting user:', error)
      // Check if error is about deleting an admin
      const errorMessage = error.response?.data?.error || error.message || t('users.deleteErrorMessage')
      addToast({
        type: 'error',
        title: t('users.loadErrorTitle'),
        message: errorMessage,
        duration: 5000
      })
    } finally {
      setDeletingUser(false)
    }
  }

  const handleToggleActive = async (user: UserWithRoles, newValue: boolean) => {
    const userId = user.id
    const previousValue = (user as any).is_active
    
    // Optimistic update in UI
    setUsers(prev => prev.map(u => 
      u.id === userId ? ({ ...u, is_active: newValue } as any) : u
    ))
    
    // Also update selectedUser if it's the same user (for profile modal)
    if (selectedUser && selectedUser.id === userId) {
      setSelectedUser(prev => prev ? ({ ...prev, is_active: newValue } as any) : prev)
    }
    
    setUpdatingStatusUserId(userId)

    try {
      await userManagementApi.updateUser(userId, { is_active: newValue })
      addToast({
        type: 'success',
        title: t('common.success'),
        message: newValue ? t('users.messages.userActivated', 'User activated') : t('users.messages.userDeactivated', 'User deactivated'),
        duration: 3000
      })
    } catch (error: any) {
      console.error('Error updating user status:', error)
      // Revert on error
      setUsers(prev => prev.map(u => 
        u.id === userId ? ({ ...u, is_active: previousValue } as any) : u
      ))
      
      // Also revert selectedUser if it's the same user
      if (selectedUser && selectedUser.id === userId) {
        setSelectedUser(prev => prev ? ({ ...prev, is_active: previousValue } as any) : prev)
      }
      
      addToast({
        type: 'error',
        title: t('users.loadErrorTitle'),
        message: t('users.saveUserError'),
        duration: 5000
      })
    } finally {
      setUpdatingStatusUserId(null)
    }
  }

  // Helper function to check if a user is an admin
  const isUserAdmin = (user: UserWithRoles): boolean => {
    // Check if user has is_staff flag (organization admins typically have this)
    if (user.is_staff) {
      return true
    }
    // Check if user has the "Admin" role (case-insensitive)
    return user.roles.some(role => role.role_name?.toLowerCase() === 'admin')
  }

  const openDeleteModal = (user: UserWithRoles) => {
    // Prevent deleting own account
    if (currentUser && currentUser.id === user.id) {
      addToast({
        type: 'error',
        title: t('users.cannotDeleteSelf', 'Cannot Delete Account'),
        message: t('users.cannotDeleteSelfMessage', 'You cannot delete your own account.'),
        duration: 5000
      })
      return
    }

    // Prevent admins from deleting other admins (only superusers can delete admins)
    if (isUserAdmin(user) && !(currentUser as any)?.is_superuser) {
      addToast({
        type: 'error',
        title: t('users.cannotDeleteAdmin', 'Cannot Delete Admin'),
        message: t('users.cannotDeleteAdminMessage', 'You cannot delete another admin. Only superusers can delete admins.'),
        duration: 5000
      })
      return
    }

    setSelectedUser(user)
    setShowDeleteModal(true)
  }

  const openEditModal = (user: UserWithRoles) => {
    // Prevent editing own profile from User Management
    if (currentUser && currentUser.id === user.id) {
      addToast({
        type: 'info',
        title: t('users.currentProfile', 'Your Profile'),
        message: t('users.editFromSettingsMessage', 'Please edit your profile from the Settings page.'),
        duration: 5000
      })
      return
    }
    setSelectedUser(user)
    setShowEditModal(true)
  }

  const openRoleModal = (user: UserWithRoles) => {
    // Prevent editing own profile roles from User Management
    if (currentUser && currentUser.id === user.id) {
      addToast({
        type: 'info',
        title: t('users.currentProfile', 'Your Profile'),
        message: t('users.editFromSettingsMessage', 'Please edit your profile from the Settings page.'),
        duration: 5000
      })
      return
    }
    setSelectedUser(user)
    setShowRoleModal(true)
  }

  const openProfileModal = (user: UserWithRoles) => {
    // Prevent opening profile modal for own profile
    if (currentUser && currentUser.id === user.id) {
      addToast({
        type: 'info',
        title: t('users.currentProfile', 'Your Profile'),
        message: t('users.editFromSettingsMessage', 'Please edit your profile from the Settings page.'),
        duration: 5000
      })
      return
    }
    setSelectedUser(user)
    setShowProfileModal(true)
  }

  // Filter users based on search and filters
  const filteredUsers = (users || []).filter(user => {
    const matchesSearch = (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (user.full_name && user.full_name.toLowerCase().includes(searchTerm.toLowerCase()))
    
    const matchesRole = !filterRole || user.roles.some(role => role.role_name === filterRole)
    
    const matchesStatus = !filterStatus || 
      (filterStatus === 'active' && !!(user as any).is_active) ||
      (filterStatus === 'inactive' && !(user as any).is_active)
    
    return matchesSearch && matchesRole && matchesStatus
  })

  // Pagination slices
  const total = filteredUsers.length
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const currentPage = Math.min(page, totalPages)
  const pagedUsers = filteredUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  useEffect(() => {
    // Reset to first page when filters/search change
    setPage(1)
  }, [searchTerm, filterRole, filterStatus])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-[1149px]:space-y-4 max-[1149px]:px-4 max-[1149px]:py-4">
      <PageHeader title={t('users.title')} />

      {/* Toolbar with condensed filters */}
      {/* Toolbar moved into table header below */}

      {/* Users Data Table */}
      <Card className="max-[1149px]:border">
        <CardHeader className="max-[1149px]:p-4">
          <div className="flex items-center justify-between gap-3 max-[1149px]:flex-col max-[1149px]:items-stretch max-[1149px]:gap-3">
            <div className="flex items-center gap-2 max-[1149px]:justify-center">
              <CardTitle className="flex items-center text-foreground max-[1149px]:text-base">
                <User className="h-5 w-5 mr-2 max-[1149px]:h-4 max-[1149px]:w-4" />
                {t('users.listTitle')} ({total})
              </CardTitle>
            </div>
            <div className="flex items-center gap-2 max-[1149px]:w-full max-[1149px]:flex-col max-[1149px]:gap-2">
              <div className="relative w-72 md:w-96 lg:w-[28rem] max-[1149px]:w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                <Input
                  placeholder={t('users.searchUsers')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 max-[1149px]:pl-9 max-[1149px]:h-9 max-[1149px]:text-sm"
                />
              </div>
              <div className="flex items-center gap-2 max-[1149px]:w-full max-[1149px]:justify-between">
                <Dropdown
                  trigger={
                    <Button variant="outline" className="flex items-center gap-2 max-[1149px]:h-9 max-[1149px]:w-9 max-[1149px]:p-0 flex-shrink-0">
                      <Filter className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                      <span className="max-[1149px]:hidden">{t('common.filters')}</span>
                    </Button>
                  }
                >
                  <div className="p-3 space-y-3 max-[1149px]:p-2 max-[1149px]:space-y-2">
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1 max-[1149px]:text-[10px]">{t('users.filterByRole')}</label>
                      <select
                        value={filterRole}
                        onChange={(e) => setFilterRole(e.target.value)}
                        className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md text-sm max-[1149px]:text-xs max-[1149px]:py-1.5"
                      >
                        <option value="">{t('common.all')}</option>
                        {(roles || []).map(role => (
                          <option key={role.id} value={role.name}>{role.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-muted-foreground mb-1 max-[1149px]:text-[10px]">{t('users.filterByStatus')}</label>
                      <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md text-sm max-[1149px]:text-xs max-[1149px]:py-1.5"
                      >
                        <option value="">{t('common.all')}</option>
                        <option value="active">{t('users.activeUsers')}</option>
                        <option value="inactive">{t('users.inactiveUsers')}</option>
                      </select>
                    </div>
                    <div className="flex gap-2 pt-1 max-[1149px]:pt-0">
                      <Button variant="outline" onClick={() => { setFilterRole(''); setFilterStatus('') }} className="flex-1 max-[1149px]:h-9 max-[1149px]:text-sm">
                        {t('common.clear')}
                      </Button>
                      <Button onClick={() => { /* dropdown auto-closes on click via component */ }} className="flex-1 max-[1149px]:h-9 max-[1149px]:text-sm">
                        {t('common.apply')}
                      </Button>
                    </div>
                  </div>
                </Dropdown>
                <Button onClick={() => setShowCreateModal(true)} className="max-[1149px]:flex-1 max-[1149px]:h-9 max-[1149px]:text-sm">
                  <Plus className="h-4 w-4 mr-2 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                  <span className="max-[1149px]:text-sm">{t('users.createUser')}</span>
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="max-[1149px]:p-4">
          <div className="overflow-x-auto max-[1149px]:-mx-4 max-[1149px]:px-4">
            <table className="min-w-full text-sm max-[1149px]:text-xs">
              <thead className="bg-muted/40">
                <tr className="text-left text-muted-foreground">
                  <th className="px-3 py-2 max-[1149px]:px-2 max-[1149px]:py-1.5 max-[1149px]:text-xs">{t('common.name')}</th>
                  <th className="px-3 py-2 max-[1149px]:px-2 max-[1149px]:py-1.5 max-[1149px]:text-xs">{t('common.email')}</th>
                  <th className="px-3 py-2 max-[1149px]:px-2 max-[1149px]:py-1.5 max-[1149px]:text-xs">{t('common.roles')}</th>
                  <th className="px-3 py-2 max-[1149px]:px-2 max-[1149px]:py-1.5 max-[1149px]:text-xs">{t('common.status')}</th>
                  <th className="px-3 py-2 text-right max-[1149px]:px-2 max-[1149px]:py-1.5 max-[1149px]:text-xs">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {pagedUsers.map((user) => {
                  const isCurrentUser = currentUser && currentUser.id === user.id
                  const isTargetUserAdmin = isUserAdmin(user)
                  const isCurrentUserSuperuser = (currentUser as any)?.is_superuser || false
                  const canDeleteUser = !isCurrentUser && (!isTargetUserAdmin || isCurrentUserSuperuser)
                  return (
                  <tr 
                    key={user.id} 
                    className={`border-t max-[1149px]:border-t ${
                      isCurrentUser ? 'bg-muted/30 dark:bg-muted/20' : ''
                    }`}
                  >
                    <td className="px-3 py-2 font-medium max-[1149px]:px-2 max-[1149px]:py-1.5 max-[1149px]:text-xs truncate max-w-[120px]">
                      <div className="flex items-center gap-2">
                        {isCurrentUser ? (
                          <span
                            className="text-left truncate flex-1 text-muted-foreground"
                            title={t('users.editFromSettings', 'Edit from Settings')}
                          >
                            {user.full_name || user.email}
                          </span>
                        ) : (
                          <button
                            onClick={() => openProfileModal(user)}
                            className="text-left hover:text-primary transition-colors cursor-pointer truncate flex-1"
                            title={t('users.viewProfile', 'View profile')}
                          >
                            {user.full_name || user.email}
                          </button>
                        )}
                        {isCurrentUser && (
                          <Badge 
                            variant="secondary" 
                            className="flex items-center gap-1 text-xs max-[1149px]:text-[10px] flex-shrink-0"
                            title={t('users.currentProfile', 'This is your profile. Edit from Settings.')}
                          >
                            <UserCircle className="h-3 w-3 max-[1149px]:h-2.5 max-[1149px]:w-2.5" />
                            <span className="max-[1149px]:hidden">{t('users.you', 'You')}</span>
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground flex items-center max-[1149px]:px-2 max-[1149px]:py-1.5">
                      <Mail className="h-3 w-3 mr-1 flex-shrink-0 max-[1149px]:h-2.5 max-[1149px]:w-2.5" />
                      <span className="truncate max-w-[150px] max-[1149px]:text-xs">{user.email}</span>
                    </td>
                    <td className="px-3 py-2 max-[1149px]:px-2 max-[1149px]:py-1.5">
                      <div className="flex flex-wrap gap-1 max-[1149px]:gap-0.5">
                        {user.roles.map((userRole) => (
                          <Badge 
                            key={userRole.id}
                            style={{ backgroundColor: userRole.role_color, color: 'white' }}
                            className="text-xs max-[1149px]:text-[10px] max-[1149px]:px-1 max-[1149px]:py-0"
                          >
                            {userRole.role_name}
                          </Badge>
                        ))}
                        {user.roles.length === 0 && (
                          <span className="text-xs text-muted-foreground max-[1149px]:text-[10px]">
                            {t('users.noRoles')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 max-[1149px]:px-2 max-[1149px]:py-1.5">
                      <div className="inline-flex items-center gap-1.5">
                        <span
                          className={`h-2 w-2 rounded-full ${
                            (user as any).is_active 
                              ? 'bg-green-500 dark:bg-green-400' 
                              : 'bg-red-500 dark:bg-red-400'
                          }`}
                        ></span>
                        <span className="text-xs text-muted-foreground">
                          {(user as any).is_active ? t('common.active') : t('common.inactive')}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2 max-[1149px]:px-2 max-[1149px]:py-1.5">
                      <div className="flex justify-end gap-1 max-[1149px]:gap-0.5">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => openRoleModal(user)} 
                          className="max-[1149px]:h-7 max-[1149px]:w-7 max-[1149px]:p-0"
                          disabled={isCurrentUser}
                          title={isCurrentUser ? t('users.editFromSettings', 'Edit from Settings') : ''}
                        >
                          <UserCheck className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => openEditModal(user)} 
                          className={`max-[1149px]:h-7 max-[1149px]:w-7 max-[1149px]:p-0 ${
                            isCurrentUser ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          disabled={isCurrentUser}
                          title={isCurrentUser ? t('users.editFromSettings', 'Edit from Settings') : t('users.editUser', 'Edit user')}
                        >
                          <Edit className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteModal(user)}
                          className={`text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 max-[1149px]:h-7 max-[1149px]:w-7 max-[1149px]:p-0 ${
                            !canDeleteUser ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          disabled={!canDeleteUser}
                          title={
                            isCurrentUser 
                              ? t('users.cannotDeleteSelf', 'Cannot delete your own account')
                              : isTargetUserAdmin && !isCurrentUserSuperuser
                              ? t('users.cannotDeleteAdmin', 'Cannot delete another admin')
                              : t('users.deleteUser', 'Delete user')
                          }
                        >
                          <Trash2 className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                        </Button>
                        {isCurrentUser && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/settings')}
                            className="max-[1149px]:h-7 max-[1149px]:w-7 max-[1149px]:p-0"
                            title={t('users.goToSettings', 'Go to Settings to edit your profile')}
                          >
                            <Settings className="h-4 w-4 max-[1149px]:h-3.5 max-[1149px]:w-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )})}
                {pagedUsers.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-muted-foreground max-[1149px]:py-6 max-[1149px]:text-sm">{t('users.noUsersFound')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 max-[1149px]:mt-3 max-[1149px]:flex-col max-[1149px]:items-stretch max-[1149px]:gap-3">
              <div className="text-xs text-muted-foreground max-[1149px]:text-center max-[1149px]:text-[10px]">
                {t('common.showing')} {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, total)} {t('common.of')} {total}
              </div>
              <div className="flex items-center gap-2 max-[1149px]:w-full max-[1149px]:justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="max-[1149px]:flex-1 max-[1149px]:h-8 max-[1149px]:text-xs"
                >
                  {t('common.previous')}
                </Button>
                <span className="text-sm max-[1149px]:text-xs">{currentPage} / {totalPages}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="max-[1149px]:flex-1 max-[1149px]:h-8 max-[1149px]:text-xs"
                >
                  {t('common.next')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Form Modal */}
      <UserForm
        isOpen={showCreateModal || showEditModal}
        onClose={() => {
          setShowCreateModal(false)
          setShowEditModal(false)
          setSelectedUser(null)
        }}
        user={selectedUser}
        onSuccess={handleFormSuccess}
      />

      {/* Role Assignment Modal */}
      <RoleAssignmentModal
        isOpen={showRoleModal}
        onClose={() => {
          setShowRoleModal(false)
          setSelectedUser(null)
        }}
        user={selectedUser}
        onSuccess={handleFormSuccess}
      />

      {/* Delete User Modal */}
      <DeleteUserModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false)
          setSelectedUser(null)
        }}
        onConfirm={handleDeleteUser}
        user={selectedUser}
        loading={deletingUser}
      />

      {/* User Profile Modal */}
      <UserProfileModal
        isOpen={showProfileModal}
        onClose={() => {
          setShowProfileModal(false)
          setSelectedUser(null)
        }}
        user={selectedUser}
        onToggleActive={selectedUser && currentUser && currentUser.id === selectedUser.id ? undefined : handleToggleActive}
        updatingStatusUserId={updatingStatusUserId}
      />
    </div>
  )
}
